<script setup lang="ts">
import { ref, computed, onMounted, watch, type Ref } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import moment from 'moment'
import { useI18n } from 'vue-i18n'

// Components
import LifeyrisrettindiCalculations from '@/components/pensionRights/LifeyrisrettindiCalculations.vue'
import LifeyrisgattTable from '@/components/pensionRights/LifeyrisgattTable.vue'
import LinkBox from '@/components/shared/LinkBox.vue'
import BaseCard from '@/components/shared/BaseCard.vue'

// Store
import { useJoakimStore } from '@/stores/useJoakimStore'

// ---------- Types ----------
type Eingreidsla = {
  erMedEingreidslu: boolean
  greidsluManudur?: string
  greidsla?: number
}

type RettindiRow = { aldur: number; rettindi: number }

// If you pass user in as a prop (optional)
const props = defineProps<{ user: { username: string; sjodfelagi?: any } | null }>()

// ---------- Setup ----------
const { t } = useI18n()
const route = useRoute()
const joakim = useJoakimStore()

// Keep reactivity + proper types from Pinia
const {
  reiknudrettindi,
  ermedeingreidslu,
} = storeToRefs(joakim) as {
  reiknudrettindi: Ref<RettindiRow[] | null>
  ermedeingreidslu: Ref<Eingreidsla | null>
}

// Local state
const tabModel = ref('tab-lifeyrisrettindi-gildi')
const lifeyrisgatt = ref<any[]>([])        // array, not null → fewer TS guards
const Idgjold = ref<any | null>(null)
const filteredIdgjold = ref<any[]>([])     // array, not null
const year = ref<number>(moment().year())
const rettindiValue = ref<number | null>(null)
const personAge = ref<number | null>(null)
const loading = ref<boolean>(true)

// Derived flags for top info box
const erBarnaLifeyri = ref(false)
const erElliLifeyri = ref(false)
const erMakaLifeyri = ref(false)
const erOrorkuLifeyri = ref(false)

const IsFramtidarRettindi = computed(
  () => !!(reiknudrettindi.value && reiknudrettindi.value.length)
)

// ---------- Utils ----------
function formatCurrency(n?: number | string | null) {
  if (n == null || n === '') return '0'
  const num = typeof n === 'string' ? Number(n) : n
  try {
    return new Intl.NumberFormat('is-IS', { maximumFractionDigits: 0 }).format(num || 0)
  } catch {
    return String(num ?? 0)
  }
}

function lifeyrisgattAccepted() {
  return localStorage.getItem('lifeyrisgatt') === 'true'
}

function getAge(): number {
  const username = props.user?.username || ''
  const day = username.substring(0, 2)
  const month = username.substring(2, 4)
  const yr = username.substring(4, 6)
  const birthday = `${month}/${day}/${yr}`
  const bday = moment(birthday).format('MM/DD/YYYY')
  const today = moment().format('MM/DD/YYYY')
  const personage = moment.duration(moment(today).diff(moment(bday)))
  return (personage as any)._data.years
}

async function lifeyrisAccept() {
  localStorage.setItem('lifeyrisgatt', 'true')
  await fetchFromLifeyrisgatt()
}

// ---------- Data helpers ----------
function getBaseCurrentValue(): number {
  const age = getAge()
  let baseCurrentValue = 0

  const src = (reiknudrettindi.value ?? []) as RettindiRow[]
  const list = src.filter(item => (age <= 67 ? item.aldur === 67 : item.aldur === 70))
  if (list[0]) baseCurrentValue = list[0].rettindi ?? 0

  if (lifeyrisgatt.value.length > 0 && lifeyrisgattAccepted()) {
    let sum = 0
    const targetAge = age <= 67 ? 67 : 70
    lifeyrisgatt.value.forEach((element: any) => {
      const mg = element?.sjodFelagi?.rettindi?.manadargreidsla ?? []
      const match = mg.find((ele: any) => ele.aldur === targetAge)
      if (match && element?.tegund === 1) sum += match.einingar ?? 0
    })
    baseCurrentValue += sum
  }
  return baseCurrentValue
}

function getlatestIdjgoldPayment() {
  const data = Idgjold.value
  if (data?.idgjold?.length > 0) return data.idgjold[0].idgjaldaManudur
}

function filterTable(val?: number) {
  if (!Idgjold.value) return
  const sereignFilter = [
    'X203T','X202T','X201T','X201','X201B','X201L',
    'X202','X202B','X202L','X203','X203B','X203L',
    'X201U','X202U','X203U'
  ]
  const base = Idgjold.value.idgjold ?? []
  const byYear = val ? base.filter((x: any) => moment(x.idgjaldaManudur).year() === val) : base
  filteredIdgjold.value = byYear.filter((x: any) => !sereignFilter.includes(x.deildNr))
  if (val) year.value = val
}

async function getLifeyriType() {
  const targetYear = moment().month() === 0 ? moment().year() - 1 : moment().year()
  const resultdata = await joakim.getGreidslusoguArs(targetYear)
  const greidslur = (resultdata as any)?.greidslur as any[] | undefined

  const tegundir: string[] = []
  if (greidslur?.length) {
    const lastMonth = greidslur[greidslur.length - 1]
    const perSjod = lastMonth?.greidslurPerSjod ?? []
    perSjod.forEach((g: any) => tegundir.push(g.tegund))
  }

  erBarnaLifeyri.value  = tegundir.some(t => t?.startsWith('Ba'))
  erElliLifeyri.value   = tegundir.some(t => t?.startsWith('El'))
  erMakaLifeyri.value   = tegundir.some(t => t?.startsWith('Ma'))
  erOrorkuLifeyri.value = tegundir.some(t => t?.startsWith('Ör'))
}

async function fetchFromLifeyrisgatt() {
  const gattinData = await joakim.getLifeyrisgatt()
  const data = (gattinData as any).data
  if (data) {
    lifeyrisgatt.value = data.lifeyrir ?? []
    rettindiValue.value = getBaseCurrentValue()
  }
}

// ---------- Lifecycle ----------
onMounted(async () => {
  loading.value = true

  await joakim.getReiknudRettindi()
  personAge.value = getAge()

  if (lifeyrisgattAccepted()) {
    await fetchFromLifeyrisgatt()
  }

  await joakim.getSameign()
  await joakim.ermedEingreidsluLifeyris()

  // NOTE: make sure your store's getIdgjold sets idgjold.value = result.data
  Idgjold.value = await joakim.getIdgjold()
  if (Idgjold.value) {
    const idParam = (route.params as any).id
    if (idParam) {
      filterTable(Number(idParam))
      year.value = Number(idParam)
    } else {
      filterTable()
    }
  }

  await getLifeyriType()
  rettindiValue.value = getBaseCurrentValue()
  loading.value = false
})

// ---------- Watchers ----------
watch(reiknudrettindi, () => {
  rettindiValue.value = getBaseCurrentValue()
})

watch(year, (val) => {
  filterTable(val)
})

const translatedLinks = computed(() => [
  { title: t('umsoknundirsida_ellilifeyrir_link'), url: '/umsoknir/lifeyrir/ellilifeyrir' },
  { title: t('umsoknir_eydublod_barnalifeyri_hlekkur_texti'), url: '/umsoknir/lifeyrir/barnalifeyrir' },
  { title: t('umsoknir_eydublod_makalifeyrir_hlekkur_texti'), url: '/umsoknir/lifeyrir/makalifeyrir' },
  { title: t('umsoknir_eydublod_endurhaefingarororkulifeyri_hlekkur_texti'), url: '/umsoknir/lifeyrir/ororkuumsokn' },
])
</script>

<template>
  <div>
    <v-row>
      <v-col cols="12">
        <v-tabs
          class="subpage__tabs"
          v-model="tabModel"
          bg-color="transparent"
          grow
        >
          <v-tab :value="'tab-lifeyrisrettindi-gildi'">
            {{ t('lifeyrisrettindi_gildi') }}
          </v-tab>
          <v-tab :value="'tab-sameign-rettindi'">
            {{ t('lifeyrisrettindi_lifeyrisgattin') }}
            <span class="small">{{ t('lifeyrisrettindi_lifeyrisgattin_text') }}</span>
          </v-tab>
        </v-tabs>

        <v-window v-model="tabModel" class="mt-4" touch>
          <v-window-item :value="'tab-lifeyrisrettindi-gildi'">
            <BaseCard v-if="erElliLifeyri || erOrorkuLifeyri" class="mb-4">
              <div v-if="erOrorkuLifeyri" class="info__text">
                {{ t('lifeyrisrettindi_ororku') }}
              </div>
              <div v-if="erElliLifeyri && personAge != null && personAge < 67" class="info__text">
                {{ t('lifeyrisrettindi_elli') }}
              </div>
              <div v-if="erElliLifeyri && personAge != null && personAge >= 67" class="info__text">
                {{ t('lifeyrisrettindi_elli_67') }}
              </div>
            </BaseCard>

            <LifeyrisrettindiCalculations
              v-if="!erElliLifeyri && !erOrorkuLifeyri"
              :userAge="String(personAge ?? '')"
              :show="IsFramtidarRettindi && !(ermedeingreidslu?.erMedEingreidslu)"
            />

            <BaseCard v-if="ermedeingreidslu?.erMedEingreidslu" class="eingreidsla-card">
              <v-row>
                <v-col cols="12" md="4" lg="3">
                  <div class="date__text">
                    <div class="date__label">{{ t('rettindi_dagsetning_eingreidslu') }}</div>
                    <div class="date__value">
                      {{ (ermedeingreidslu?.greidsluManudur || '').split('-')[1] }}.{{ (ermedeingreidslu?.greidsluManudur || '').split('-')[0] }}
                    </div>
                  </div>
                  <div class="date__text">
                    <div class="date__label">{{ t('rettindi_upphaed_eingreidslu') }}</div>
                    <div class="date__value">{{ formatCurrency(ermedeingreidslu?.greidsla) }} kr.</div>
                  </div>
                </v-col>
                <v-col cols="12" md="8" lg="9">
                  <div class="description__text">
                    {{ t('rettindi_eingreidsla_texti') }}
                  </div>
                </v-col>
              </v-row>
            </BaseCard>
          </v-window-item>
            <v-window-item :value="'tab-sameign-rettindi'">
              <LifeyrisgattTable
                :username="props.user?.username || ''"
                :gildiLatesPayment="getlatestIdjgoldPayment"
                @fetchData="fetchFromLifeyrisgatt"
              />
            </v-window-item>

        </v-window>
      </v-col>
    </v-row>

    <!-- Link Card -->
    <v-row>
      <v-col cols="12">
        <LinkBox
          title="Umsóknir"
          :links="translatedLinks"
        />
      </v-col>
    </v-row>
  </div>
</template>

<style scoped lang="scss">
.small { 
  font-size: 0.8em; 
}

.info__text { 
  color: #194458; 
  font-size: 20px; 
  font-weight: 600;
  line-height: 1.5;
  
  &:not(:last-child) {
    margin-bottom: 12px;
  }
}

.mb-4 {
  margin-bottom: 1.5rem;
}

.eingreidsla-card {
  margin-top: 20px;
  
  .date__text {
    margin-bottom: 24px;
    
    &:last-child {
      margin-bottom: 0;
      
      @media (max-width: 959px) {
        margin-bottom: 20px;
      }
    }
  }
  
  .date__label {
    font-weight: 600;
    color: #194458;
    font-size: 13px;
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    opacity: 0.75;
  }
  
  .date__value {
    color: #194458;
    font-size: 22px;
    font-weight: 700;
  }
  
  .description__text {
    color: #555;
    font-size: 16px;
    line-height: 1.7;
    
    @media (max-width: 959px) {
      margin-top: 20px;
      padding-top: 24px;
      border-top: 2px solid #e8e8e8;
    }
  }
}

.m-t-70 {
  margin-top: 70px;
}

// Dark mode support
body.dark-mode {
  .info__text {
    color: #4fc3f7;
  }
  
  .eingreidsla-card {
    .date__label {
      color: #4fc3f7;
    }
    
    .date__value {
      color: #e8e8e8;
    }
    
    .description__text {
      color: #b8b8b8;
      
      @media (max-width: 959px) {
        border-top-color: #3a3a3a;
      }
    }
  }
}
</style>
